import {
	CircularProgress,
	ListItemButton,
	ListItemIcon,
	ListItemText,
	Stack,
	Typography,
	useTheme,
} from "@mui/material";
import { FileText } from "lucide-react";
import type { FC } from "react";
import { PinExistingContractItemStatus } from "web_myplann/Goals/Components/PinContract/PinExistingContractItemStatus";
import { ALLOCATION_MAX, ALLOCATION_MIN } from "web_myplann/Goals/constants";
import { formatAttachedGoals } from "web_myplann/Goals/Helpers/attachedGoalsLabel";
import { useFormatGoalAmount } from "web_myplann/Goals/Helpers/formatGoalAmount";
import type { GoalPortfolioListItemResponseDTO } from "web_myplann/Infrastructure/Api/Api";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";
import { IconBox } from "web_myplann/Shared/Components/IconBox";
import { joinTruthy } from "web_myplann/Shared/Utils/textFormatters";

type Props = {
	portfolio: GoalPortfolioListItemResponseDTO;
	pinnedAllocation: number | null;
	hasDivider: boolean;
	isPinning: boolean;
	isBusy: boolean;
	onSelect: (portfolio: GoalPortfolioListItemResponseDTO) => void;
};

export const PinExistingContractItem: FC<Props> = ({
	portfolio,
	pinnedAllocation,
	hasDivider,
	isPinning,
	isBusy,
	onSelect,
}) => {
	const { palette, iconSize, spacingTokens } = useTheme();
	const { t } = useResource();
	const formatAmount = useFormatGoalAmount();

	const available = Math.max(0, ALLOCATION_MAX - portfolio.usagePercent);
	const isFull = available < ALLOCATION_MIN && pinnedAllocation === null;

	const attachedGoalsLabel = formatAttachedGoals(
		portfolio.attachedToGoals,
		(name, percent) =>
			t(Resources.Goals.Detail.Solution.Pin.Existing.attachedGoal, {
				name,
				percent,
			}),
	);

	return (
		<ListItemButton
			disableRipple
			divider={hasDivider}
			disabled={isFull || (isBusy && !isPinning)}
			onClick={() => onSelect(portfolio)}
			sx={{
				gap: spacingTokens.md,
				px: spacingTokens.lg,
				py: spacingTokens.md,
				borderColor: "surface.border",
				"&:active": { bgcolor: "grey.50" },
			}}
		>
			<ListItemIcon sx={{ minWidth: 0 }}>
				<IconBox bg={palette.grey[100]}>
					{isPinning ? (
						<CircularProgress size={iconSize.md} />
					) : (
						<FileText size={iconSize.md} color={palette.grey[500]} />
					)}
				</IconBox>
			</ListItemIcon>
			<ListItemText
				primary={portfolio.title}
				secondary={
					<>
						<Typography
							variant="caption"
							color="grey.500"
							display="block"
							noWrap
						>
							{joinTruthy([portfolio.producer, portfolio.domain])}
						</Typography>
						<Stack mt={spacingTokens.xxs}>
							<PinExistingContractItemStatus
								isFull={isFull}
								available={available}
								pinnedAllocation={pinnedAllocation}
							/>
							{isFull && attachedGoalsLabel && (
								<Typography variant="caption" color="grey.500">
									{t(
										Resources.Goals.Detail.Solution.Pin.Existing.attachedGoals,
										{ goals: attachedGoalsLabel },
									)}
								</Typography>
							)}
						</Stack>
					</>
				}
				slotProps={{
					primary: { noWrap: true },
					secondary: { component: "div" },
				}}
				sx={{ my: 0 }}
			/>
			<Typography variant="bodyMedium" tabular flexShrink={0}>
				{formatAmount(portfolio.portfolioValue ?? 0)}{" "}
				{t(Resources.Goals.Amount.currencySign)}
			</Typography>
		</ListItemButton>
	);
};
